// jsaForm/v1/fallProtection.actions.js

// Helper to get reset fields for fall protection
const getResetFallProtectionFields = () => ({
  fpEquipReqYes: false,
  fpEquipBodyHarness: false,
  fpEquipShockLanyard: false,
  fpEquipRetracLanyard: false,
  fpEquipOtherInfo: "",
});

export const makeJsaV1FallProtectionActions = ({ setJsaUi, bump, emitJsaPatch }) => ({
  updateJsaFallProtectionYes(jsaData, value, emitPatch) {
    // Capture inspection items before reset (for delete patches)
    const inspectionItemsToDelete = !value
      ? [...jsaData.jsaFallProtection.fallProtectionInspectionList]
      : [];

    jsaData.jsaFallProtection.updateFpEquipReqYes(value);

    const fpYesFields = { fpEquipReqYes: value };
    if (!value) {
      Object.assign(fpYesFields, getResetFallProtectionFields());
    } else {
      fpYesFields.fpEquipReqNo = false;
    }

    if (emitPatch) {
      // Send delete patches for each inspection item
      inspectionItemsToDelete.forEach((item) => {
        const deletePatch = item.toPatch({}, jsaData.jsaBasicInfo.rowId, "remove");
        emitJsaPatch(deletePatch, emitPatch);
      });
      // Send update patch for scalar fields
      const patch = jsaData.jsaFallProtection.toPatch(fpYesFields, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    setJsaUi({
      v1: (prev) => ({
        ...prev,
        fallProtection: {
          ...(prev?.fallProtection || {}),
          showFallProtectionSection: value,
        },
      }),
    });
    bump();
  },

  updateJsaFallProtectionNo(jsaData, value, emitPatch) {
    // Capture inspection items before reset (for delete patches)
    const inspectionItemsToDelete = value
      ? [...jsaData.jsaFallProtection.fallProtectionInspectionList]
      : [];

    jsaData.jsaFallProtection.updateFpEquipReqNo(value);

    const fpNoFields = { fpEquipReqNo: value };
    if (value) {
      fpNoFields.fpEquipReqYes = false;
      Object.assign(fpNoFields, getResetFallProtectionFields());
    }

    if (emitPatch) {
      // Send delete patches for each inspection item
      inspectionItemsToDelete.forEach((item) => {
        const deletePatch = item.toPatch({}, jsaData.jsaBasicInfo.rowId, "remove");
        emitJsaPatch(deletePatch, emitPatch);
      });
      // Send update patch for scalar fields
      const patch = jsaData.jsaFallProtection.toPatch(fpNoFields, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    if (value) {
      setJsaUi({
        v1: (prev) => ({
          ...prev,
          fallProtection: {
            ...(prev?.fallProtection || {}),
            showFallProtectionSection: false,
          },
        }),
      });
    } else {
      bump();
    }
  },

  updateJsaFallProtectionBodyHarness(jsaData, value, emitPatch) {
    jsaData.jsaFallProtection.updateFpEquipBodyHarness(value);
    if (emitPatch) {
      const patch = jsaData.jsaFallProtection.toPatch({ fpEquipBodyHarness: value }, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaFallProtectionShockLanyard(jsaData, value, emitPatch) {
    jsaData.jsaFallProtection.updateFpEquipShockLanyard(value);
    if (emitPatch) {
      const patch = jsaData.jsaFallProtection.toPatch({ fpEquipShockLanyard: value }, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaFallProtectionRetracLanyard(jsaData, value, emitPatch) {
    jsaData.jsaFallProtection.updateFpEquipRetracLanyard(value);
    if (emitPatch) {
      const patch = jsaData.jsaFallProtection.toPatch({ fpEquipRetracLanyard: value }, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaFallProtectionOtherInfo(jsaData, value, emitPatch) {
    jsaData.jsaFallProtection.updateFpEquipOtherInfo(value);
    if (emitPatch) {
      const patch = jsaData.jsaFallProtection.toPatch({ fpEquipOtherInfo: value }, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  addJsaFallProtectionInspectionListItem(jsaData, emitPatch) {
    const newRow = jsaData.jsaFallProtection.addFallProtectionInspectionListItem();
    if (emitPatch && newRow) {
      // toPatch includes id: this.rowId automatically
      const patch = newRow.toPatch({}, jsaData.jsaBasicInfo.rowId, "add");
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  removeJsaFallProtectionInspectionListItem(jsaData, inspection, emitPatch) {
    const row = jsaData.jsaFallProtection.fallProtectionInspectionList.find(
      (i) => i.rowId === inspection.rowId
    );
    jsaData.jsaFallProtection.removeFallProtectionInspectionList(inspection);
    if (emitPatch && row) {
      // toPatch includes id: this.rowId which is the database ID
      const patch = row.toPatch({}, jsaData.jsaBasicInfo.rowId, "remove");
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaFallProtectionInspectionListItemEquipmentSn(
    jsaData,
    inspection,
    value,
    emitPatch
  ) {
    const row = jsaData.jsaFallProtection.updateFallProtectionInspectionListItemEquipmentSn(
      inspection,
      value
    );
    if (emitPatch && row) {
      // Use correct field name from model's toPatch fieldMap
      const patch = row.toPatch({ equipmentSerialNumber: value }, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaFallProtectionInspectionListItemAcceptReject(
    jsaData,
    inspection,
    inspectionType,
    value,
    emitPatch
  ) {
    // Find the row before updating
    const row = jsaData.jsaFallProtection.fallProtectionInspectionList.find(
      (item) => item.rowId === inspection.rowId
    );

    // Update the model
    jsaData.jsaFallProtection.updateFallProtectionInspectionListItemAcceptReject(
      inspection,
      inspectionType,
      value
    );

    if (emitPatch && row) {
      // Build patch data based on the inspection type
      // The inspectionType is like "HardwareInspectionAccept" or "HardwareInspectionReject"
      const patchData = {};

      // Convert inspectionType to camelCase field name for patch
      const fieldName = inspectionType.charAt(0).toLowerCase() + inspectionType.slice(1);
      patchData[fieldName] = value;

      // If value is true, we need to also clear the opposite (accept clears reject, reject clears accept)
      if (value) {
        if (inspectionType.endsWith("Accept")) {
          const rejectField = inspectionType.replace("Accept", "Reject");
          const rejectFieldName = rejectField.charAt(0).toLowerCase() + rejectField.slice(1);
          patchData[rejectFieldName] = false;
        } else if (inspectionType.endsWith("Reject")) {
          const acceptField = inspectionType.replace("Reject", "Accept");
          const acceptFieldName = acceptField.charAt(0).toLowerCase() + acceptField.slice(1);
          patchData[acceptFieldName] = false;
        }
      }

      const patch = row.toPatch(patchData, jsaData.jsaBasicInfo.rowId);
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
});
